import { css } from '@emotion/react'
import { colorTokens } from '../../styles/tokens'

/**
 * Stat-card styles.
 *
 * These cards are designed as equal-sized carousel slides: every card uses the
 * same vertical hierarchy (kicker → focal value → supporting copy → footer),
 * only the `tone` changes.
 *
 * Colours come from the shared `colorTokens` theme. The one exception is the
 * `mint` tone: the theme has no light-mint surface, so it falls back to the
 * lightest neutral (`slate100`) — revisit if a mint token is added.
 */
type Tone = 'light' | 'dark' | 'mint'

export const styles = {
  card: css`
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    border-radius: 24px;
    padding: 40px;
    overflow: hidden;
		text-wrap-style: balance;
  `,
  fill: css`
    flex: 1;
    height: 100%;
  `,
  headerRow: css`
    align-items: center;
  `,
  body: css`
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
  `,

  tone: {
    light: css`
      background: ${colorTokens.white};
      color: ${colorTokens.slate900};
      border: 1px solid ${colorTokens.slate200};
      box-shadow: 0 24px 56px -34px rgba(20, 30, 20, 0.24);
    `,
    dark: css`
      background: ${colorTokens.green900};
      color: ${colorTokens.white};
    `,
    mint: css`
      background: ${colorTokens.slate100};
      color: ${colorTokens.green200};
    `,
  } satisfies Record<Tone, ReturnType<typeof css>>,

  dot: css`
    width: 11px;
    height: 11px;
    border-radius: 50%;
    flex-shrink: 0;
  `,
  dotTone: {
    light: css`
      background: ${colorTokens.green100};
    `,
    dark: css`
      background: ${colorTokens.green400};
    `,
    mint: css`
      background: ${colorTokens.green100};
    `,
  } satisfies Record<Tone, ReturnType<typeof css>>,

  kicker: css`
    font-weight: 600;
    font-size: 13px;
    line-height: 1;
    letter-spacing: 2px;
    text-transform: uppercase;
  `,
  kickerTone: {
    light: css`
      color: ${colorTokens.slate400};
    `,
    dark: css`
      color: ${colorTokens.green400};
    `,
    mint: css`
      color: ${colorTokens.green300};
    `,
  } satisfies Record<Tone, ReturnType<typeof css>>,

  // Focal value — the single large number/word on each card.
  metric: css`
    font-weight: 800;
    font-size: 76px;
    line-height: 0.9;
    letter-spacing: -2px;
  `,
  metricSm: css`
    font-weight: 800;
    font-size: 44px;
    line-height: 1;
    letter-spacing: -0.6px;
  `,
  metricLabel: css`
    font-weight: 600;
    font-size: 22px;
    line-height: 1.25;
		font-weight: 400;
    text-wrap-style: balance;
		`,
		description: css`
    text-wrap-style: balance;
    font-size: 18px;
    line-height: 1.45;
  `,
  descriptionTone: {
    light: css`
      color: ${colorTokens.slate500};
    `,
    dark: css`
      color: ${colorTokens.green500};
    `,
    mint: css`
      color: ${colorTokens.green300};
    `,
  } satisfies Record<Tone, ReturnType<typeof css>>,

  footer: css`
    margin-top: auto;
  `,

  subValue: css`
    font-weight: 800;
    font-size: 34px;
    line-height: 1;
  `,
  subLabel: css`
    font-size: 15px;
    font-weight: 500;
    color: ${colorTokens.green400};
  `,

  // Pills (compliance card)
  pillRow: css`
    flex-wrap: wrap;
  `,
  pillSolid: css`
    background: ${colorTokens.green100};
		border: 1.5px solid ${colorTokens.green100};
    color: ${colorTokens.white};
    font-weight: 500;
    font-size: 12px;
    line-height: 1;
    padding: .5em 1em;
    border-radius: 999px;
  `,
  pillOutline: css`
    background: ${colorTokens.white};
    color: ${colorTokens.green100};
    border: 1.5px solid ${colorTokens.green500};
    font-weight: 700;
    font-size: 12px;
    line-height: 1;
    padding: .5em 1em;
    border-radius: 999px;
  `,

  link: css`
    font-weight: 700;
    font-size: 18px;
    white-space: nowrap;
  `,

  // Cabinet Grotesk 900 display treatment — matches the home hero and the
  // login headings (see dashboard/login/styles.ts `headingFont`).
  headingFont: css`
    font-family:
      var(--font-cabinet-grotesk), 'system-ui', Arial, sans-serif !important;
    font-weight: 900 !important;
  `,

  // Icon chip matching the HomePage feature cards: a soft green500 @ 20% tile
  // behind a `color="success"` MUI icon.
  iconChip: css`
    flex-shrink: 0;
    width: 50px;
    height: 50px;
    padding: 10px;
    border-radius: 6px;
    background: rgba(165, 197, 166, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      width: 30px;
      height: 30px;
    }
  `,
}
